const TEMPLATES = ['intersection', 'roundabout', 'parking', 'two-lane', 'sign'] as const;
const COMPASS = ['north', 'south', 'east', 'west'] as const;
const INTENTS = ['straight', 'left', 'right', 'stop', 'yield'] as const;
const SIGN_KINDS = ['stop', 'yield', 'warning'] as const;

export type DiagramTemplate = (typeof TEMPLATES)[number];
export type DiagramCompass = (typeof COMPASS)[number];
export type DiagramIntent = (typeof INTENTS)[number];
export type DiagramSignKind = (typeof SIGN_KINDS)[number];

export interface DiagramVehicle {
  label: string;
  from: DiagramCompass;
  intent: DiagramIntent;
  kind?: 'car' | 'bus';
  lights?: boolean;
  back?: boolean;
}

export interface DiagramSign {
  kind: DiagramSignKind;
  at?: DiagramCompass;
}

/** One step of a diagram. Later cards reveal more of the same picture. */
export interface DiagramLayer {
  id: string;
  vehicles?: DiagramVehicle[];
  signs?: DiagramSign[];
  arrows?: boolean;
  labels?: boolean;
  crosswalk?: boolean;
  walker?: boolean;
}

/** Lightweight top-down diagram. The React view draws this as inline SVG. */
export interface RoadDiagramSpec {
  template: DiagramTemplate;
  vehicles?: DiagramVehicle[];
  signs?: DiagramSign[];
  crosswalk?: boolean;
  median?: boolean;
  walker?: boolean;
  rain?: boolean;
  sign?: DiagramSignKind;
  /** Shared id when consecutive cards build one picture. */
  build?: string;
  /** Layer ids this card adds. */
  reveal?: string[];
  layers?: DiagramLayer[];
  /** False hides intent arrows. Omitted means show them. */
  arrows?: boolean;
  /** False hides A/B/C labels. Omitted means show them. */
  labels?: boolean;
}

export interface LessonSection {
  heading_en: string;
  heading_my: string;
  body_en: string;
  body_my: string;
  diagram?: RoadDiagramSpec | null;
  image_url?: string | null;
  caption_en?: string;
  caption_my?: string;
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[]): T | null {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : null;
}

function readVehicles(value: unknown): DiagramVehicle[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const vehicle = item as Record<string, unknown>;
    const from = oneOf(vehicle.from, COMPASS);
    const intent = oneOf(vehicle.intent, INTENTS);
    const label = String(vehicle.label || '').trim().slice(0, 3);
    if (!from || !intent || !label) return [];
    const kind = vehicle.kind === 'bus' ? 'bus' : 'car';
    return [{
      label,
      from,
      intent,
      kind,
      lights: Boolean(vehicle.lights),
      back: Boolean(vehicle.back),
    } satisfies DiagramVehicle];
  });
}

function readSigns(value: unknown): DiagramSign[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const sign = item as Record<string, unknown>;
    const kind = oneOf(sign.kind, SIGN_KINDS);
    if (!kind) return [];
    const at = oneOf(sign.at, COMPASS);
    return [at ? { kind, at } : { kind }];
  });
}

function readLayers(value: unknown): DiagramLayer[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const row = item as Record<string, unknown>;
    const id = String(row.id || '').trim().slice(0, 24);
    if (!id) return [];
    return [{
      id,
      vehicles: readVehicles(row.vehicles),
      signs: readSigns(row.signs),
      arrows: Boolean(row.arrows),
      labels: Boolean(row.labels),
      crosswalk: Boolean(row.crosswalk),
      walker: Boolean(row.walker),
    } satisfies DiagramLayer];
  });
}

/** Show only the layers named in reveal. A later vehicle with the same label replaces the earlier one. */
export function applyDiagramReveal(spec: RoadDiagramSpec, reveal: readonly string[]): RoadDiagramSpec {
  if (!spec.layers?.length) {
    return { ...spec, arrows: spec.arrows !== false, labels: spec.labels !== false };
  }
  const show = new Set(reveal);
  const vehicles = [...(spec.vehicles || [])];
  const signs = [...(spec.signs || [])];
  let arrows = false;
  let labels = false;
  let crosswalk = Boolean(spec.crosswalk);
  let walker = Boolean(spec.walker);
  for (const layer of spec.layers) {
    if (!show.has(layer.id)) continue;
    for (const vehicle of layer.vehicles || []) {
      const at = vehicles.findIndex((item) => item.label === vehicle.label);
      if (at >= 0) vehicles[at] = vehicle;
      else vehicles.push(vehicle);
    }
    signs.push(...(layer.signs || []));
    if (layer.arrows) arrows = true;
    if (layer.labels) labels = true;
    if (layer.crosswalk) crosswalk = true;
    if (layer.walker) walker = true;
  }
  return { ...spec, vehicles, signs, arrows, labels, crosswalk, walker };
}

export function normalizeDiagram(value: unknown): RoadDiagramSpec | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  const template = oneOf(row.template, TEMPLATES);
  if (!template) return null;
  const layers = readLayers(row.layers);
  const reveal = Array.isArray(row.reveal)
    ? row.reveal.map((item) => String(item).trim().slice(0, 24)).filter(Boolean)
    : [];
  const build = String(row.build || '').trim().slice(0, 40);
  return {
    template,
    vehicles: readVehicles(row.vehicles),
    signs: readSigns(row.signs),
    crosswalk: Boolean(row.crosswalk),
    median: Boolean(row.median),
    walker: Boolean(row.walker),
    rain: Boolean(row.rain),
    sign: oneOf(row.sign, SIGN_KINDS) || undefined,
    build: build || undefined,
    reveal: reveal.length ? reveal : undefined,
    layers: layers.length ? layers : undefined,
    arrows: row.arrows === false ? false : undefined,
    labels: row.labels === false ? false : undefined,
  };
}

export function normalizeSections(value: unknown): LessonSection[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const row = item as Record<string, unknown>;
    const section: LessonSection = {
      heading_en: String(row.heading_en || ''),
      heading_my: String(row.heading_my || ''),
      body_en: String(row.body_en || ''),
      body_my: String(row.body_my || ''),
      diagram: normalizeDiagram(row.diagram),
      image_url: row.image_url ? String(row.image_url) : null,
      caption_en: String(row.caption_en || ''),
      caption_my: String(row.caption_my || ''),
    };
    const hasCopy = section.heading_en || section.heading_my || section.body_en || section.body_my;
    if (!hasCopy && !section.diagram && !section.image_url) return [];
    return [section];
  });
}
